import { getCore } from '../../../client/core/getCore';
import type { DynamicClient } from '../../../client/types';
import { retryOnFail } from '../../../utils/retryOnFail';
import type { WalletBookSchema } from '../legacyWalletBook.types';
import { fetchWithTimeout } from './fetchWithTimeout';
import { walletBookCacheStorageKeySchema } from './walletBookCacheStorageKeySchema';

/**
 * The maximum number of retry attempts.
 */
const MAX_RETRIES = 3;

/**
 * The delay between retries in milliseconds.
 */
const RETRY_DELAY_MS = 100;

/**
 * Fetches the legacy wallet book from Dynamic's CDN endpoint.
 *
 * This function implements a two-tier fallback strategy:
 * 1. Primary: Fetch from CDN endpoint (with retries)
 * 2. Secondary: Use cached version from localStorage if available
 *
 * @param client - The Dynamic client instance.
 * @returns A promise that resolves to the wallet book data.
 * @throws If both CDN fetch and cache retrieval fail.
 */
export const fetchLegacyWalletBook = async (
  client: DynamicClient
): Promise<WalletBookSchema> => {
  const core = getCore(client);

  core.logger.debug('[fetchLegacyWalletBook] Fetching wallet book...');

  // Try to fetch from CDN with retry logic
  try {
    const walletBook = await retryOnFail({
      delay: RETRY_DELAY_MS,
      fn: async () => fetchWithTimeout(client),
      maxRetries: MAX_RETRIES,
    });

    await core.storage.setItem(walletBookCacheStorageKeySchema, walletBook);

    core.logger.debug(
      '[fetchLegacyWalletBook] Successfully fetched wallet book from CDN'
    );

    return walletBook;
  } catch (error) {
    core.logger.debug(
      '[fetchLegacyWalletBook] Failed to fetch from CDN, trying cache...',
      error
    );

    // Try to get from cache
    const cachedWalletBook = await core.storage.getItem(
      walletBookCacheStorageKeySchema
    );

    if (cachedWalletBook) {
      core.logger.debug('[fetchLegacyWalletBook] Using cached wallet book');

      return cachedWalletBook as WalletBookSchema;
    }

    // If both CDN and cache fail, throw the original error
    core.logger.debug(
      '[fetchLegacyWalletBook] Failed to fetch from CDN and no cache available'
    );

    throw error;
  }
};
